// @ts-check
// Cartão de entrada "Devem percorrer o fluxo" (spec 2.0.0, fluxos.md seção 20.5; contrato 09 de fluxos, seção 17):
// sem entrada, o botão tracejado "+ Selecionar uma entrada" e o link "Definir entrada depois"; entrada manual, o selo e
// a explicação; com gatilhos, o selo escuro de quando entram ("vão atender" ou "já atendem") e uma linha por gatilho,
// separadas por "ou". Fluxo já ativado mostra quantos entraram e quantos estão no fluxo agora.
import { html } from 'htm/preact';
import { Icon } from '../../ui/icon.js';
import { FlowCountLink } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowEntry} FlowEntry
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowTrigger} FlowTrigger
 * @typedef {import('../../../../shared/flows/flow-summary.js').FlowSummaryLookups} FlowSummaryLookups
 * @typedef {import('../../../../shared/flows/flow-problems.js').FlowProblem} FlowProblem
 */

const MAXIMUM_PHRASES = 3;

/**
 * Frase de um gatilho para o cartão e o modal de ativação.
 * @param {FlowTrigger} trigger @param {FlowSummaryLookups} lookups @param {(fieldName: string, value: string) => string | null} optionLabel
 * @returns {{ lead: string, values: string[], more: number }}
 */
export function describeTrigger(trigger, lookups, optionLabel) {
  if (trigger.kind === 'AUDIENCE') {
    const phrases = trigger.audience ? lookups.audiencePhrases?.(trigger.audience) ?? [] : [];
    return { lead: t('flows.entry.card.audience'), values: phrases.slice(0, MAXIMUM_PHRASES), more: Math.max(0, phrases.length - MAXIMUM_PHRASES) };
  }
  if (trigger.kind === 'STAGE_CHANGED') {
    const stages = trigger.toStages.map((stage) => lookups.stageLabel?.(stage) ?? stage);
    return { lead: t('flows.entry.card.stage'), values: stages.length > 0 ? [stages.join(', ')] : [t('flows.entry.card.noStage')], more: 0 };
  }
  const sources = trigger.sources.map((source) => optionLabel('leadSource', source) ?? source);
  return { lead: t('flows.entry.card.leadCreated'), values: [sources.length > 0 ? sources.join(', ') : t('flows.entry.card.anySource')], more: 0 };
}

/**
 * @param {{ entry: FlowEntry, lookups: FlowSummaryLookups, problems?: FlowProblem[], isFocused?: boolean, isReadOnly?: boolean, isSelected?: boolean,
 *   counts?: { entered: number, inFlow: number } | null, flowId?: string | null, onEdit?: () => void, onSetManualOnly?: () => void }} props
 */
export function FlowEntryCard({ entry, lookups, problems = [], isFocused = false, isReadOnly = false, isSelected = false, counts = null, flowId = null, onEdit, onSetManualOnly }) {
  const optionLabel = (/** @type {string} */ fieldName, /** @type {string} */ value) => lookups.optionLabel?.(fieldName, value) ?? null;
  const hasError = problems.some((problem) => problem.severity === 'ERROR');
  const edit = (/** @type {MouseEvent} */ event) => {
    event.stopPropagation();
    onEdit?.();
  };
  /** @type {any} */
  let body;
  if (entry.mode === 'UNSET') {
    body = isReadOnly
      ? html`<p class="flow-entry-empty-text">${t('flows.entry.notSet')}</p>`
      : html`<button type="button" class="flow-entry-select" tabindex="-1" onClick=${edit}><${Icon} name="IconPlus" size=${16} /><span>${t('flows.entry.select')}</span></button>
        <button type="button" class="flow-link-button" tabindex="-1" onClick=${(/** @type {MouseEvent} */ event) => {
          event.stopPropagation();
          onSetManualOnly?.();
        }}>${t('flows.entry.later')}</button>`;
  } else if (entry.mode === 'MANUAL_ONLY') {
    body = html`<div class="flow-entry-box">
        <span class="flow-entry-badge">${t('flows.entry.manualBadge')}</span>
        <p class="flow-entry-line">${t('flows.entry.manualText')}</p>
      </div>
      ${isReadOnly ? null : html`<button type="button" class="flow-link-button" tabindex="-1" onClick=${edit}>${t('flows.entry.select')}</button>`}`;
  } else {
    body = html`<div class="flow-entry-box">
      <span class="flow-entry-badge">${t(`flows.entry.timing.${entry.timing}`)}</span>
      ${entry.triggers.length === 0 ? html`<p class="flow-entry-line">${t('flows.entry.noTriggers')}</p>` : null}
      ${entry.triggers.map((trigger, position) => {
        const described = describeTrigger(trigger, lookups, optionLabel);
        return html`<div key=${trigger.id} class="flow-entry-trigger">
          ${position > 0 ? html`<span class="flow-entry-or">${t('flows.entry.or')}</span>` : null}
          <p class="flow-entry-line">${described.lead}</p>
          ${described.values.map((value) => html`<p class="flow-entry-value">${value}</p>`)}
          ${described.more > 0 ? html`<p class="flow-entry-more">${t('flows.entry.card.more', { count: described.more })}</p>` : null}
        </div>`;
      })}
    </div>`;
  }
  return html`<div class="flow-entry-card" role="treeitem" aria-level="1" aria-selected=${isSelected ? 'true' : 'false'} tabindex=${isFocused ? 0 : -1}
    data-focus-id="entry" data-problem=${hasError ? 'error' : undefined} data-selected=${isSelected ? 'true' : undefined}
    aria-label=${t('flows.entry.ariaLabel')} onClick=${isReadOnly ? undefined : edit}>
    <div class="flow-entry-header">
      <span class="flow-entry-title">${t('flows.entry.title')}</span>
      ${entry.mode !== 'UNSET' && !isReadOnly
        ? html`<button type="button" class="flow-entry-edit" tabindex="-1" onClick=${edit}><${Icon} name="IconPencil" size=${14} /><span>${t('flows.entry.edit')}</span></button>`
        : null}
    </div>
    ${body}
    ${counts && flowId
      ? html`<div class="flow-entry-counts">
          <${FlowCountLink} flowId=${flowId} nodeKey="entry" metric="ENTERED" value=${counts.entered} className="flow-entry-count"
            label=${t('flows.counts.entered', { count: counts.entered })} tabIndex=${isFocused ? 0 : -1}>
            <span>${t('flows.counts.entered', { count: counts.entered })}</span>
          <//>
          <span aria-hidden="true">·</span>
          <${FlowCountLink} flowId=${flowId} nodeKey="entry" metric="IN_FLOW" value=${counts.inFlow} className="flow-entry-count"
            label=${t('flows.counts.inFlow', { count: counts.inFlow })} tabIndex=${isFocused ? 0 : -1}>
            <span>${t('flows.counts.inFlow', { count: counts.inFlow })}</span>
          <//>
        </div>`
      : null}
  </div>`;
}
